import * as Sentry from '@sentry/react';

/**
 * Error reporting for the app (InGo ADR 0026, the console's twin; the privacy
 * filter after Jambo's, `D:/OS/references/reusable-features.md`, "privacy-
 * filtered Sentry client observability").
 *
 * Off unless the build was given a DSN (`REACT_APP_SENTRY_DSN`): a developer's
 * build and every test send nothing. A browser DSN is public by design: it
 * lets anyone write events, nothing more.
 *
 * Unlike the console (staff only, where Rio chose full request data), the app
 * is used by clients and riders, so it sends nobody: no user and no IP
 * (`sendDefaultPii: false`), and the scrubber takes out of every event,
 * breadcrumb and log what names or locates a person or opens an account:
 * addresses, emails, phone numbers, positions, every query string (a typed
 * address rides in one), tokens, passwords, the reset links' tokens.
 *
 * Errors only, no tracing: a traced request carries `sentry-trace` and
 * `baggage` headers to the API, a cross-origin request from the phone, which
 * the API's CORS would then have to allow. JavaScript only: a native crash is
 * Android's (Play's vitals) until a native SDK is chosen.
 */
export function startObservability(env = process.env) {
  const dsn = env.REACT_APP_SENTRY_DSN;

  // No DSN, no integrations built at all.
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: env.REACT_APP_SENTRY_ENVIRONMENT || env.NODE_ENV,
    release: env.REACT_APP_BUILD || undefined,
    sendDefaultPii: false,
    integrations: [Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] })],
    enableLogs: true,
    tracesSampleRate: 0,
    beforeSend: (event) => scrub(event),
    beforeBreadcrumb: (breadcrumb) => scrub(breadcrumb),
    beforeSendLog: (log) => scrub(log),
    ignoreErrors: [
      // A request left behind by leaving a screen: not a fault.
      'AbortError',
      'CanceledError',
      /^(abort: )?Abort$/,
      'Non-Error promise rejection captured',
    ],
  });

  return true;
}

export const REDACTED = '[redacted]';

/** Keys whose values are a credential or name or locate a person. */
const SECRET_KEY = /password|secret|token|authorization|cookie|api_?key|private_?key|signature|credential|email|phone|latitude|longitude|^lat$|^lng$|address|recipient|full_?name|first_?name|last_?name/i;

/** The app's and the API's paths that carry a token (App.js; armkit/accounts). */
const TOKEN_PATHS = [/(\/driver\/reset\/)[^/?#\s"']+/g, /(\/reset\/)[^/?#\s"']+/g, /(\/password-resets\/)[^/?#\s"']+/g];

/** A string with credentials, contact details, positions and query strings taken out. */
export function scrubText(text) {
  let out = text;
  for (const pattern of TOKEN_PATHS) {
    out = out.replace(pattern, `$1${REDACTED}`);
  }

  return (
    out
      // Every query string: a typed address or an email rides in one.
      .replace(/\?[^\s"'#]*/g, `?${REDACTED}`)
      .replace(/Bearer\s+[^\s"',}]+/gi, `Bearer ${REDACTED}`)
      .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, REDACTED)
      // A Sanctum token: id|secret.
      .replace(/\b\d+\|[A-Za-z0-9]{20,}\b/g, REDACTED)
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, REDACTED)
      // A number with its country code, or Zimbabwe's ten digits from 0.
      .replace(/\+\d[\d\s-]{7,}\d/g, REDACTED)
      .replace(/\b0\d{9}\b/g, REDACTED)
      // A coordinate: four decimals or more.
      .replace(/-?\b\d{1,3}\.\d{4,}\b/g, REDACTED)
  );
}

/** Anything Sentry is about to send, scrubbed by key and in every string. */
export function scrub(value, depth = 0) {
  if (typeof value === 'string') return scrubText(value);
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1));

  // Plain data only: the SDK's own objects pass untouched.
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;

  const out = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = SECRET_KEY.test(key) ? REDACTED : scrub(item, depth + 1);
  }

  return out;
}
